import { useListResourceTypes } from '@client-app-sdk';
import { ResourceTypeClientModel } from '@client-app-sdk/api/v1';
import { Combobox, ComboboxOption } from '@common/ui/Combobox/Combobox';
import { useCallback, useEffect, useMemo, useState } from 'react';

type ResourceTypeComboboxProps = {
  integrationId: string;
  onChange: (value: ComboboxOption | null) => void;
  value: ComboboxOption | null;
  setIsMultiplePermission?: (value: boolean) => void;
  disabled?: boolean;
  sessionId?: string;
};

export function ResourceTypeCombobox({
  integrationId,
  onChange,
  value,
  setIsMultiplePermission,
  disabled,
  sessionId,
}: ResourceTypeComboboxProps) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useMemo(() => search, [search]);
  const { data, isFetching, hasNextPage, fetchNextPage } = useListResourceTypes({
    integrationId,
    limit: 100,
    search: debouncedSearch,
    sessionId,
  });

  const fetchMore = useCallback(() => {
    if (hasNextPage) fetchNextPage();
  }, [hasNextPage, fetchNextPage]);

  const handleMultiplePermission = useCallback(
    (resourceTypeId?: string) => {
      const resourceType = data?.pages.flatMap((page: { data: ResourceTypeClientModel[] }) =>
        page.data.find((rt) => rt.id === resourceTypeId),
      )[0];

      if (resourceType) {
        setIsMultiplePermission?.(resourceType.allow_multiple_permissions);
      }
    },
    [data, setIsMultiplePermission],
  );

  const handleOnChange = useCallback(
    (val: ComboboxOption | null) => {
      onChange(val);
      handleMultiplePermission(val?.value);
    },
    [onChange, handleMultiplePermission],
  );

  const resourceTypeComboboxOptions = useMemo(() => {
    return (
      data?.pages.flatMap((page: { data: ResourceTypeClientModel[] }) =>
        page.data.map((resourceType) => ({
          value: resourceType.id,
          label: resourceType.name,
          icon: resourceType.icons.svg,
        })),
      ) ?? []
    );
  }, [data]);

  useEffect(() => {
    if (value) handleMultiplePermission(value.value);
  }, [value, handleMultiplePermission]);

  return (
    <Combobox
      value={value}
      loading={isFetching}
      fetchMore={fetchMore}
      label="Resource type"
      onInputChange={setSearch}
      disabled={!integrationId || disabled}
      placeholder="Select resource type"
      options={resourceTypeComboboxOptions}
      inputValue={search ? search : value?.label}
      onChange={(val) => handleOnChange(Array.isArray(val) ? null : val)}
    />
  );
}
